import {AppBarPage} from "../../components/AppBarPage";
import AppBar from "@mui/material/AppBar";
import * as React from "react";
import {
    Button,
    Grid,
    Paper, SelectChangeEvent,
    Table, TableBody, TableCell,
    TableContainer,
    TableHead, TablePagination, TableRow, TextField, Toolbar
} from "@mui/material";
import {useEffect, useState} from "react";
import Typography from "@mui/material/Typography";
import dayjs from "dayjs";
import {DateTimeUtils} from "../../utils/DateTimeUtils";
import {strings} from "../../localization/Localization";
import Box from "@mui/material/Box";
import "../../App.css"
import {FullPageLoadingCircle} from "../../components/FullPageLoadingCircle";
import {Routes} from "../../router/Routes";
import {useNavigate, useSearchParams} from "react-router-dom";
import {defaultRowsPerPageOptions} from "../../common/Constants";
import {PaginationPlus} from "../../components/PaginationPlus";
import {PlaylistService} from "../../services/PlaylistService";
import {NumberParser} from "../../utils/NumberParser";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { Select, MenuItem, FormControl } from "@mui/material";

function ImportedSongsContent() {

    const probabilityOptions: { value: string; label: string }[] = [
        { value: "boosted", label: strings.probabilityBoosted },
        { value: "medium", label: strings.probabilityMedium },
        { value: "low", label: strings.probabilityLow },
    ]

    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    const title = searchParams.get("title") ?? "";
    const artist = searchParams.get("artist") ?? "";
    const album = searchParams.get("album") ?? "";
    const url = searchParams.get("url") ?? "";
    const probability = searchParams.get("probability") ?? "";
    const itemGroup = searchParams.get("itemGroup") ?? "";
    const dateCreated = searchParams.get("dateCreated") ?? "";
    const page = NumberParser.parseNumber(searchParams.get("page"), 0);
    const rowsPerPage = NumberParser.parseNumber(searchParams.get("rowsPerPage"), 10);
    const sort = searchParams.get("sort") ?? "";

    const [publicTargetPlaylistItems, setPublicTargetPlaylistItems] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [totalElements, setTotalElements] = useState(0);

    const handleDashboardClick = () => {
        navigate(Routes.HOME);
    };

    const handleSummaryReportClick = () => {
        navigate(Routes.SUMMARY_REPORT);
    };

    const handleScrapedDataClick = () => {
        navigate(Routes.SCRAPED_DATA);
    };

    function setSearchParam(key: string, value: string | null | undefined) {
        if (value !== null && value !== undefined && value !== "") {
            searchParams.set(key, value);
        } else {
            searchParams.delete(key);
        }
        setSearchParams(searchParams);
    }

    function setPage(page: number) {
        setSearchParam("page", page.toString());
    }

    const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchParam("title", event.target.value);
        setPage(0);
    };

    const handleArtistChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchParam("artist", event.target.value);
        setPage(0);
    };

    const handleAlbumChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchParam("album", event.target.value);
        setPage(0);
    };

    const handleUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchParam("url", event.target.value);
        setPage(0);
    };

    const handleProbabilityChange = (event: React.ChangeEvent<HTMLInputElement> | SelectChangeEvent) => {
        setSearchParam("probability", event.target.value);
        setPage(0);
    };

    const handleItemGroupChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchParam("itemGroup", event.target.value);
        setPage(0);
    };

    const handleDateCreatedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchParam("dateCreated", event.target.value);
        setPage(0);
    };

    useEffect(() => {
        async function fetchData() {
            let loadingTimeout: NodeJS.Timeout | null = null;

            loadingTimeout = setTimeout(() => {
                setIsLoading(true);
            }, 300);

            try {
                const formattedDateCreated =
                    dateCreated && dayjs(dateCreated).isValid()
                        ? DateTimeUtils.formatDateDayJs(dayjs(dateCreated))
                        : undefined;

                const result = await PlaylistService.allPagedWithPublicTargetPlaylist(
                    title,
                    artist,
                    album,
                    url,
                    probability,
                    itemGroup,
                    formattedDateCreated,
                    page,
                    rowsPerPage,
                    sort
                );

                setPublicTargetPlaylistItems(result.content);
                setTotalElements(result.totalElements);
            } finally {
                if (loadingTimeout) {
                    clearTimeout(loadingTimeout);
                }
                setIsLoading(false);
            }
        }

        fetchData();
    }, [title, artist, album, url, probability, itemGroup, dateCreated, page, rowsPerPage, sort]);


    function handleChangePage(event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) {
        setPage(newPage);
    }

    function handleChangeRowsPerPage(event: React.ChangeEvent<HTMLInputElement>) {
        setSearchParam("rowsPerPage", event.target.value);
        setPage(0);
    }

    const handleSort = (field: string) => {
        const currentSort = searchParams.get("sort") ?? "";
        let newSort = `${field},asc`;

        if (currentSort.startsWith(`${field},asc`)) {
            newSort = `${field},desc`;
        } else if (currentSort.startsWith(`${field},desc`)) {
            newSort = '';
        }

        if (field === "probability") {
            searchParams.delete("probability");
        }

        if (newSort) {
            searchParams.set("sort", newSort);
        } else {
            searchParams.delete("sort");
        }
        setSearchParams(searchParams);
    };

    const getSortIcon = (field: string) => {
        const currentSort = searchParams.get("sort") ?? "";
        if (currentSort.startsWith(`${field},asc`)) {
            return <ArrowUpwardIcon fontSize="small" />;
        } else if (currentSort.startsWith(`${field},desc`)) {
            return <ArrowDownwardIcon fontSize="small" />;
        }
        return null;
    };

    return (
        <>
            <FullPageLoadingCircle loading={isLoading} />

            <AppBar
                position="absolute"
                sx={{ marginTop: "65.5px", minHeight: "65.5px" }}
                color="secondary"
            >
                <Toolbar>
                    <Button
                        onClick={handleDashboardClick}
                        sx={{
                            marginLeft: "20px",
                            cursor: "pointer",
                            fontWeight: "bold",
                            color: "black",
                            backgroundColor: "#efefef",
                            width: "10%",
                            '&:hover': {
                                backgroundColor: "lightgray",
                            },
                        }}
                    >
                        {strings.dashboard}
                    </Button>
                    <Button
                        onClick={handleSummaryReportClick}
                        sx={{
                            marginLeft: "20px",
                            cursor: "pointer",
                            fontWeight: "bold",
                            color: "black",
                            backgroundColor: "#efefef",
                            width: "10%",
                            '&:hover': {
                                backgroundColor: "lightgray",
                            },
                        }}
                    >
                        {strings.summaryReport}
                    </Button>
                    <Button
                        onClick={handleScrapedDataClick}
                        sx={{
                            marginLeft: "20px",
                            cursor: "pointer",
                            fontWeight: "bold",
                            color: "black",
                            backgroundColor: "#efefef",
                            width: "10%",
                            '&:hover': {
                                backgroundColor: "lightgray",
                            },
                        }}
                    >
                        {strings.scrapedData}
                    </Button>
                </Toolbar>
            </AppBar>

            <Box>
                <Typography style={{
                    fontSize: "2rem",
                    fontWeight: "bold",
                    marginTop: "108px",
                    marginBottom: "70px"
                }}>
                    {strings.importedSongs}
                </Typography>

                <div className="mainDiv" style={{
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "wrap",
                    marginLeft: "40px",
                    width: "95%",
                    gap: "35px",
                    marginTop: "25px",
                    marginBottom: "25px",
                    justifyContent: "flex-start"
                }}>
                    <div style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        flex: "1 1 calc(33.333% - 20px)",
                        minWidth: "300px",
                            maxWidth: "400px"
                    }}>
                        <Typography style={{
                            marginRight: "12px",
                            fontSize: "16px",
                            whiteSpace: "nowrap",
                            minWidth: "100px"
                        }}>
                            {strings.title}:
                        </Typography>
                        <TextField
                            name="title"
                            type="text"
                            value={title}
                            onChange={handleTitleChange}
                            fullWidth
                            FormHelperTextProps={{style: {display: 'none'}}}
                            style={{
                                border: "black",
                                borderStyle: "solid",
                                borderWidth: "1.5px",
                                borderRadius: "1px",
                                backgroundColor: "White"
                            }}
                            sx={{
                                '& .MuiFormHelperText-root': {
                                    display: 'none',
                                    margin: 0,
                                    height: 0
                                }
                            }}
                        />
                    </div>

                    <div style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        flex: "1 1 calc(33.333% - 20px)",
                        minWidth: "300px",
                             maxWidth: "400px"
                    }}>
                        <Typography style={{
                            marginRight: "12px",
                            fontSize: "16px",
                            whiteSpace: "nowrap",
                            minWidth: "100px"
                        }}>
                            {strings.artist}:
                        </Typography>
                        <TextField
                            name="artist"
                            type="text"
                            value={artist}
                            onChange={handleArtistChange}
                            fullWidth
                            FormHelperTextProps={{style: {display: 'none'}}}
                            style={{
                                border: "black",
                                borderStyle: "solid",
                                borderWidth: "1.5px",
                                borderRadius: "1px",
                                backgroundColor: "White"
                            }}
                            sx={{
                                '& .MuiFormHelperText-root': {
                                    display: 'none',
                                    margin: 0,
                                    height: 0
                                }
                            }}
                        />
                    </div>

                    <div style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        flex: "1 1 calc(33.333% - 20px)",
                        minWidth: "300px",
                             maxWidth: "400px"
                    }}>
                        <Typography style={{
                            marginRight: "12px",
                            fontSize: "16px",
                            whiteSpace: "nowrap",
                            minWidth: "100px"
                        }}>
                            {strings.album}:
                        </Typography>
                        <TextField
                            name="album"
                            type="text"
                            value={album}
                            onChange={handleAlbumChange}
                            fullWidth
                            FormHelperTextProps={{style: {display: 'none'}}}
                            style={{
                                border: "black",
                                borderStyle: "solid",
                                borderWidth: "1.5px",
                                borderRadius: "1px",
                                backgroundColor: "White"
                            }}
                            sx={{
                                '& .MuiFormHelperText-root': {
                                    display: 'none',
                                    margin: 0,
                                    height: 0
                                }
                            }}
                        />
                    </div>

                    <div style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        flex: "1 1 calc(33.333% - 20px)",
                        minWidth: "300px",
                             maxWidth: "400px"
                    }}>
                        <Typography style={{
                            marginRight: "12px",
                            fontSize: "16px",
                            whiteSpace: "nowrap",
                            minWidth: "100px"
                        }}>
                            {strings.URL}:
                        </Typography>
                        <TextField
                            name="url"
                            type="text"
                            value={url}
                            onChange={handleUrlChange}
                            fullWidth
                            FormHelperTextProps={{style: {display: 'none'}}}
                            style={{
                                border: "black",
                                borderStyle: "solid",
                                borderWidth: "1.5px",
                                borderRadius: "1px",
                                backgroundColor: "White"
                            }}
                            sx={{
                                '& .MuiFormHelperText-root': {
                                    display: 'none',
                                    margin: 0,
                                    height: 0
                                }
                            }}
                        />
                    </div>

                    <div style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        flex: "1 1 calc(33.333% - 20px)",
                        minWidth: "300px",
                        maxWidth: "400px"
                    }}>
                        <Typography style={{
                            marginRight: "12px",
                            fontSize: "16px",
                            whiteSpace: "nowrap",
                            minWidth: "100px"
                        }}>
                            {strings.probability}:
                        </Typography>
                        <FormControl
                            fullWidth
                            variant="outlined"
                            sx={{
                                border: "1.5px solid black",
                                borderRadius: "1px",
                                backgroundColor: "white",
                                "& .MuiOutlinedInput-root": {
                                    height: "55px",
                                    fontSize: "16px",
                                }
                            }}
                        >
                            <Select
                                name="probability"
                                value={probability || "all"}
                                onChange={handleProbabilityChange}
                                displayEmpty
                            >
                                {probabilityOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>


                    <div style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        flex: "1 1 calc(33.333% - 20px)",
                        minWidth: "300px",
                             maxWidth: "400px"
                    }}>
                        <Typography style={{
                            marginRight: "12px",
                            fontSize: "16px",
                            whiteSpace: "nowrap",
                            minWidth: "100px"
                        }}>
                            {strings.group}:
                        </Typography>
                        <TextField
                            name="itemGroup"
                            type="text"
                            value={itemGroup}
                            onChange={handleItemGroupChange}
                            fullWidth
                            FormHelperTextProps={{style: {display: 'none'}}}
                            style={{
                                border: "black",
                                borderStyle: "solid",
                                borderWidth: "1.5px",
                                borderRadius: "1px",
                                backgroundColor: "White"
                            }}
                            sx={{
                                '& .MuiFormHelperText-root': {
                                    display: 'none',
                                    margin: 0,
                                    height: 0
                                }
                            }}
                        />
                    </div>

                    <div style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        flex: "1 1 calc(33.333% - 20px)",
                        minWidth: "300px",
                             maxWidth: "400px"
                    }}>
                        <Typography style={{
                            marginRight: "12px",
                            fontSize: "16px",
                            whiteSpace: "nowrap",
                            minWidth: "100px"
                        }}>
                            {strings.dateImported}:
                        </Typography>
                        <TextField
                            name="dateCreated"
                            type="date"
                            value={dateCreated}
                            onChange={handleDateCreatedChange}
                            fullWidth
                            InputLabelProps={{shrink: true}}
                            FormHelperTextProps={{style: {display: 'none'}}}
                            style={{
                                border: "black",
                                borderStyle: "solid",
                                borderWidth: "1.5px",
                                borderRadius: "1px",
                                backgroundColor: "White"
                            }}
                            sx={{
                                '& .MuiFormHelperText-root': {
                                    display: 'none',
                                    margin: 0,
                                    height: 0
                                }
                            }}
                        />
                    </div>
                </div>

                <TableContainer
                    component={Paper}
                    style={{marginTop: "70px", borderRadius: "10px", marginBottom: "70px"}}
                >
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    onClick={() => handleSort('title')}
                                    style={{fontFamily: "sans-serif", cursor: "pointer", userSelect: "none"}}
                                >
                                    <div style={{display: "flex", alignItems: "center", gap: "4px"}}>
                                        {strings.title} {getSortIcon('title')}
                                    </div>
                                </TableCell>
                                <TableCell
                                    onClick={() => handleSort('artist')}
                                    style={{fontFamily: "sans-serif", cursor: "pointer", userSelect: "none"}}
                                >
                                    <div style={{display: "flex", alignItems: "center", gap: "4px"}}>
                                        {strings.artist} {getSortIcon('artist')}
                                    </div>
                                </TableCell>
                                <TableCell
                                    onClick={() => handleSort('album')}
                                    style={{fontFamily: "sans-serif", cursor: "pointer", userSelect: "none"}}
                                >
                                    <div style={{display: "flex", alignItems: "center", gap: "4px"}}>
                                        {strings.album} {getSortIcon('album')}
                                    </div>
                                </TableCell>
                                <TableCell
                                    onClick={() => handleSort('url')}
                                    style={{fontFamily: "sans-serif", cursor: "pointer", userSelect: "none"}}
                                >
                                    <div style={{display: "flex", alignItems: "center", gap: "4px"}}>
                                        {strings.URL} {getSortIcon('url')}
                                    </div>
                                </TableCell>
                                <TableCell
                                    onClick={() => handleSort('probability')}
                                    style={{fontFamily: "sans-serif", cursor: "pointer", userSelect: "none"}}
                                >
                                    <div style={{display: "flex", alignItems: "center", gap: "4px"}}>
                                        {strings.probability} {getSortIcon('probability')}
                                    </div>
                                </TableCell>
                                <TableCell
                                    onClick={() => handleSort('itemGroup')}
                                    style={{fontFamily: "sans-serif", cursor: "pointer", userSelect: "none"}}
                                >
                                    <div style={{display: "flex", alignItems: "center", gap: "4px"}}>
                                        {strings.group} {getSortIcon('itemGroup')}
                                    </div>
                                </TableCell>
                                <TableCell
                                    onClick={() => handleSort('dateCreated')}
                                    style={{fontFamily: "sans-serif", cursor: "pointer", userSelect: "none"}}
                                >
                                    <div style={{display: "flex", alignItems: "center", gap: "4px"}}>
                                        {strings.dateImported} {getSortIcon('dateCreated')}
                                    </div>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {publicTargetPlaylistItems.map((item: any) => (
                                <TableRow key={item.id} hover={true}>
                                    <TableCell style={{fontFamily: "sans-serif"}}>{item.title}</TableCell>
                                    <TableCell style={{fontFamily: "sans-serif"}}>{item.artist}</TableCell>
                                    <TableCell style={{fontFamily: "sans-serif"}}>{item.album}</TableCell>
                                    <TableCell style={{fontFamily: "sans-serif"}}>{item.url}</TableCell>
                                    <TableCell style={{fontFamily: "sans-serif"}}>{item.probability}</TableCell>
                                    <TableCell style={{fontFamily: "sans-serif"}}>{item.itemGroup}</TableCell>
                                    <TableCell
                                        style={{fontFamily: "sans-serif"}}>{DateTimeUtils.formatDate(item.dateCreated)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <Grid sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'end',
                        marginTop: "10px"
                    }}>
                        <PaginationPlus
                            totalElements={totalElements}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onChangePage={(page) => handleChangePage(null, page)}
                        />
                        <TablePagination
                            rowsPerPageOptions={defaultRowsPerPageOptions}
                            component="div"
                            count={totalElements}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </Grid>
                </TableContainer>
            </Box>
        </>
    );
}

export function ImportedSongsPage() {
    return (
        <AppBarPage
            pageTitle={strings.analytics}
            component={<ImportedSongsContent/>}
        />
    );
}
